import { WHALE_ICON_URL } from '../../shared/animalArtwork';
import { useEffect, useRef, useState, type FormEvent } from 'react';
import type {
  Account,
  AccountAnimal,
  AccountExplorer,
  CurrentPolicy,
  DistanceUnit,
} from '../../shared/types/api';
import { CompassMark } from '../components/CompassMark';
import { ErrorNotice } from '../components/ErrorNotice';
import { LoadingTrail } from '../components/LoadingTrail';
import { CURRENT_PUBLIC_POLICY_IDENTITY } from '../content/publicPolicies';
import { createOpenAccountAnimalHandler } from '../lib/accountPortalActions';
import { ApiClientError, api } from '../lib/api';

interface AccountPortalPageProps {
  onBack: () => void;
  onOpenAnimal: (entitlementId: string) => void;
  onOpenTrailBook: () => void;
}

function messageFrom(error: unknown): string {
  return error instanceof Error ? error.message : 'That trail could not be completed.';
}

function removeAccountLinkToken(name: 'verification' | 'reset') {
  if (typeof window === 'undefined') return;
  const url = new URL(window.location.href);
  url.searchParams.delete(name);
  window.history.replaceState(null, '', `${url.pathname}${url.search}${url.hash}`);
}

function accountLinkTokensFromLocation(): { verification: string | null; reset: string | null } {
  const searchParams =
    typeof window === 'undefined'
      ? new URLSearchParams()
      : new URLSearchParams(window.location.search);
  const verification = searchParams.get('verification');
  return {
    verification,
    reset: verification ? null : searchParams.get('reset'),
  };
}

const EXPLORER_BADGE_SYMBOLS: Record<AccountExplorer['badge'], string> = {
  sun: '☀',
  moon: '☾',
  star: '★',
  leaf: '❧',
  wave: '≈',
  mountain: '△',
};

const ACCOUNT_ANIMAL_TABLE_META: Record<string, { iconUrl: string; type: string }> = {
  '11111111-1111-4111-8111-111111111111': {
    iconUrl: '/art/account/penguin.png',
    type: 'Penguin',
  },
  '22222222-2222-4222-8222-222222222222': {
    iconUrl: '/art/account/turtle.png',
    type: 'Turtle',
  },
  '33333333-3333-4333-8333-333333333333': {
    iconUrl: '/art/account/elephant.png',
    type: 'Elephant',
  },
  '44444444-4444-4444-8444-444444444444': {
    iconUrl: '/art/account/dolphin.png',
    type: 'Dolphin',
  },
  '55555555-5555-4555-8555-555555555555': {
    iconUrl: WHALE_ICON_URL,
    type: 'Whale',
  },
};

type UnlockedAccountAnimal = AccountAnimal & { entitlementId: string };

function hasEntitlement(animal: AccountAnimal): animal is UnlockedAccountAnimal {
  return animal.unlocked && Boolean(animal.entitlementId);
}

function familyAnimalsForTable(
  animals: AccountAnimal[],
  selectedExplorerId?: string,
): UnlockedAccountAnimal[] {
  const familyAnimals = new Map<string, UnlockedAccountAnimal>();
  for (const animal of animals) {
    if (!hasEntitlement(animal)) continue;
    const key = animal.simulatedAnimalId ?? animal.entitlementId;
    const current = familyAnimals.get(key);
    if (!current || animal.explorerId === selectedExplorerId) familyAnimals.set(key, animal);
  }
  return [...familyAnimals.values()];
}

export function AccountPortalPage({
  onBack,
  onOpenAnimal,
  onOpenTrailBook,
}: AccountPortalPageProps) {
  const [hasAccountActionLink] = useState(() => {
    const tokens = accountLinkTokensFromLocation();
    return Boolean(tokens.verification || tokens.reset);
  });
  const [loading, setLoading] = useState(!hasAccountActionLink);
  const [account, setAccount] = useState<Account | null>(null);
  const [animals, setAnimals] = useState<AccountAnimal[]>([]);
  const [explorers, setExplorers] = useState<AccountExplorer[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [signedOutMessage, setSignedOutMessage] = useState<string | null>(null);
  const [policyGate, setPolicyGate] = useState<CurrentPolicy | null>(null);

  useEffect(() => {
    if (hasAccountActionLink) return;
    let active = true;
    api
      .getAccountPolicy()
      .then(async (policy) => {
        if (!policy.accepted) return { policy } as const;
        const current = await api.getAccount();
        const [loadedAnimals, loadedExplorers] = await Promise.all([
          api.listAccountAnimals(),
          api.listAccountExplorers(),
        ]);
        return { current, animals: loadedAnimals, explorers: loadedExplorers } as const;
      })
      .then((result) => {
        if (!active) return;
        if ('policy' in result) {
          if (result.policy) setPolicyGate(result.policy);
          return;
        }
        setAccount(result.current);
        setAnimals(result.animals);
        setExplorers(result.explorers);
      })
      .catch((reason: unknown) => {
        if (!active) return;
        if (reason instanceof ApiClientError && reason.status === 401) {
          setAccount(null);
          setAnimals([]);
          setExplorers([]);
        } else {
          setError(messageFrom(reason));
        }
      })
      .finally(() => {
        if (active) setLoading(false);
      });
    return () => {
      active = false;
    };
  }, [hasAccountActionLink]);

  if (loading) return <LoadingTrail message="Opening the family account…" />;
  if (policyGate) {
    return (
      <PolicyAcceptanceGate
        policy={policyGate}
        onAccepted={() => {
          setPolicyGate(null);
          setLoading(true);
          void Promise.all([api.getAccount(), api.listAccountAnimals(), api.listAccountExplorers()])
            .then(([current, loadedAnimals, loadedExplorers]) => {
              setAccount(current);
              setAnimals(loadedAnimals);
              setExplorers(loadedExplorers);
            })
            .catch((reason: unknown) => setError(messageFrom(reason)))
            .finally(() => setLoading(false));
        }}
        onLogout={() => {
          void api.logoutAccount().finally(() => {
            setPolicyGate(null);
            setAccount(null);
          });
        }}
      />
    );
  }
  if (!account) {
    return (
      <AccountAccess
        onBack={onBack}
        onAuthenticated={(authenticated) => {
          if (authenticated.policyAccepted) {
            onOpenTrailBook();
            return;
          }
          void api
            .getAccountPolicy()
            .then(setPolicyGate)
            .catch((reason: unknown) => setError(messageFrom(reason)));
        }}
        error={error}
        initialMessage={signedOutMessage}
      />
    );
  }
  return (
    <AccountDashboard
      account={account}
      animals={animals}
      explorers={explorers}
      onBack={onBack}
      onOpenAnimal={onOpenAnimal}
      onOpenTrailBook={onOpenTrailBook}
      onAccountChange={setAccount}
      onAnimalsChange={setAnimals}
      onExplorersChange={setExplorers}
      onLogout={(reason) => {
        setSignedOutMessage(
          reason === 'deletion_requested'
            ? 'Your deletion request was received. You are signed out, and animal-trail access has been revoked. Personal data removal will be completed separately.'
            : null,
        );
        setAccount(null);
        setAnimals([]);
        setExplorers([]);
      }}
    />
  );
}

export function PolicyAcceptanceGate({
  policy,
  onAccepted,
  onLogout,
}: {
  policy: CurrentPolicy;
  onAccepted: () => void;
  onLogout: () => void;
}) {
  const [confirmed, setConfirmed] = useState(false);
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState<string | null>(null);
  return (
    <div className="account-shell">
      <main className="account-access-shell">
        <section className="account-card" aria-labelledby="policy-acceptance-heading">
          <p className="store-kicker">Grown-up account</p>
          <h1 id="policy-acceptance-heading">Review the current account policies</h1>
          <p>
            Before using account features, review the <a href="/terms">Terms of Use</a> and{' '}
            <a href="/privacy">Privacy Notice</a>, effective September 7, 2026 (version{' '}
            {policy.version}).
          </p>
          <label className="adult-confirmation">
            <input
              type="checkbox"
              checked={confirmed}
              onChange={(event) => setConfirmed(event.target.checked)}
            />
            <span>I agree to the Terms and acknowledge the Privacy Notice.</span>
          </label>
          <button
            type="button"
            disabled={!confirmed || busy}
            onClick={() => {
              setBusy(true);
              setMessage(null);
              void api
                .acceptCurrentPolicy()
                .then(onAccepted)
                .catch((reason: unknown) => setMessage(messageFrom(reason)))
                .finally(() => setBusy(false));
            }}
          >
            {busy ? 'Saving…' : 'Accept and continue'}
          </button>
          <button type="button" className="text-action" disabled={busy} onClick={onLogout}>
            Sign out
          </button>
          {message && (
            <p className="account-message" role="status">
              {message}
            </p>
          )}
        </section>
      </main>
    </div>
  );
}

export function AccountAccess({
  onBack,
  onAuthenticated,
  error,
  initialMode = 'sign-in',
  initialMessage = null,
}: {
  onBack: () => void;
  onAuthenticated: (account: Account) => void;
  error: string | null;
  initialMode?: 'sign-in' | 'register';
  initialMessage?: string | null;
}) {
  const [accountLinkTokens] = useState(accountLinkTokensFromLocation);
  const [mode, setMode] = useState<'sign-in' | 'register'>(
    accountLinkTokens.verification || accountLinkTokens.reset ? 'sign-in' : initialMode,
  );
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [firstName, setFirstName] = useState('');
  const [code, setCode] = useState('');
  const [registrationToken, setRegistrationToken] = useState<string | null>(null);
  const [policyVersion, setPolicyVersion] = useState<string | null>(null);
  const [verificationToken, setVerificationToken] = useState<string | null>(null);
  const [registrationComplete, setRegistrationComplete] = useState(false);
  const [automaticVerificationState, setAutomaticVerificationState] = useState<
    'idle' | 'pending' | 'failed'
  >(accountLinkTokens.verification ? 'pending' : 'idle');
  const [resetToken, setResetToken] = useState<string | null>(accountLinkTokens.reset);
  const [resetPassword, setResetPassword] = useState('');
  const [message, setMessage] = useState<string | null>(
    accountLinkTokens.verification ? 'Verifying your email…' : initialMessage,
  );
  const [busy, setBusy] = useState(Boolean(accountLinkTokens.verification));
  const automaticVerificationStarted = useRef(false);
  const automaticVerificationPending = automaticVerificationState === 'pending';

  useEffect(() => {
    const token = accountLinkTokens.verification;
    if (!token || automaticVerificationStarted.current) return;
    automaticVerificationStarted.current = true;
    removeAccountLinkToken('verification');
    removeAccountLinkToken('reset');
    void api
      .verifyAccountEmail(token)
      .then(() => {
        setMode('sign-in');
        setRegistrationToken(null);
        setVerificationToken(null);
        setAutomaticVerificationState('idle');
        setMessage('Email verified. Sign in to open the family account.');
      })
      .catch((reason: unknown) => {
        setAutomaticVerificationState('failed');
        setMessage(messageFrom(reason));
      })
      .finally(() => {
        setBusy(false);
      });
  }, [accountLinkTokens.verification]);

  useEffect(() => {
    if (accountLinkTokens.reset) removeAccountLinkToken('reset');
  }, [accountLinkTokens.reset]);

  async function signIn(event: FormEvent) {
    event.preventDefault();
    setBusy(true);
    setMessage(null);
    try {
      const authenticated = await api.loginAccount(email, password);
      onAuthenticated(authenticated);
    } catch (reason) {
      setMessage(messageFrom(reason));
    } finally {
      setBusy(false);
    }
  }

  async function checkCode(event: FormEvent) {
    event.preventDefault();
    setBusy(true);
    setMessage(null);
    try {
      const intent = await api.createRegistrationIntent(code);
      setRegistrationToken(intent.registrationToken);
      setPolicyVersion(intent.policyVersion ?? null);
      setMessage('Code accepted. Now create the grown-up account.');
    } catch (reason) {
      setMessage(messageFrom(reason));
    } finally {
      setBusy(false);
    }
  }

  async function register(event: FormEvent) {
    event.preventDefault();
    if (!registrationToken) return;
    setBusy(true);
    setMessage(null);
    try {
      const result = await api.registerAccount({
        registrationToken,
        firstName,
        email,
        password,
        policyVersion: policyVersion ?? undefined,
        termsSha256: policyVersion ? CURRENT_PUBLIC_POLICY_IDENTITY.termsSha256 : undefined,
        privacySha256: policyVersion ? CURRENT_PUBLIC_POLICY_IDENTITY.privacySha256 : undefined,
      });
      setRegistrationComplete(true);
      setVerificationToken(result.developmentVerificationToken ?? null);
      setMessage(
        result.developmentVerificationToken
          ? 'Account created. Use the local verification button to finish.'
          : result.verificationEmailQueued === false
            ? 'Account created, but the verification email could not be queued. Use “Send a new verification” to try again.'
            : 'Account created. Check the adult email inbox for the verification link.',
      );
    } catch (reason) {
      setMessage(messageFrom(reason));
    } finally {
      setBusy(false);
    }
  }

  async function verifyLocalAccount() {
    if (!verificationToken) return;
    setBusy(true);
    try {
      await api.verifyAccountEmail(verificationToken);
      setMode('sign-in');
      setRegistrationToken(null);
      setVerificationToken(null);
      setAutomaticVerificationState('idle');
      setMessage('Email verified. Sign in to open the family account.');
    } catch (reason) {
      setMessage(messageFrom(reason));
    } finally {
      setBusy(false);
    }
  }

  async function requestReset() {
    if (!email) {
      setMessage('Enter the adult email address first.');
      return;
    }
    setBusy(true);
    try {
      const result = await api.requestAccountPasswordReset(email);
      setResetToken(result.developmentResetToken ?? null);
      setMessage(
        result.developmentResetToken
          ? 'Use the local reset form below to choose a new password.'
          : 'If that account is available, password reset instructions are on the way.',
      );
    } catch (reason) {
      setMessage(messageFrom(reason));
    } finally {
      setBusy(false);
    }
  }

  async function completeReset(event: FormEvent) {
    event.preventDefault();
    if (!resetToken) return;
    setBusy(true);
    try {
      await api.completeAccountPasswordReset(resetToken, resetPassword);
      setResetToken(null);
      setResetPassword('');
      removeAccountLinkToken('reset');
      setMessage('Password changed. Sign in with the new password.');
    } catch (reason) {
      setMessage(messageFrom(reason));
    } finally {
      setBusy(false);
    }
  }

  async function resendVerification() {
    if (!email) {
      setMessage('Enter the adult email address first.');
      return;
    }
    setBusy(true);
    try {
      const result = await api.resendAccountVerification(email);
      setVerificationToken(result.developmentVerificationToken ?? null);
      setMessage(
        result.developmentVerificationToken
          ? 'A new local verification is ready.'
          : 'If the account is waiting for verification, a new message is on its way.',
      );
    } catch (reason) {
      setMessage(messageFrom(reason));
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="account-shell">
      <header className="account-topbar">
        <button type="button" className="back-button" onClick={onBack}>
          ← Shop
        </button>
        <a className="brand" href="/" aria-label="Wild Trails Kids home">
          <CompassMark />
          <span>Wild Trails Kids</span>
        </a>
      </header>
      <main className="account-access-layout">
        <section className="account-welcome">
          <p className="eyebrow">Grown-up account</p>
          <h1>Keep every animal trail together.</h1>
          <p>Unlock animal friends, manage the family account, and keep access secure.</p>
          <p className="privacy-note">
            This area asks only for adult account information. Do not enter a child’s name, email,
            birthday, photo, or location.
          </p>
        </section>
        <section className="account-form-card" aria-labelledby="account-form-title">
          <div className="account-tabs" role="tablist" aria-label="Account access">
            <button
              type="button"
              role="tab"
              aria-selected={mode === 'sign-in'}
              disabled={automaticVerificationPending}
              onClick={() => {
                setMode('sign-in');
                setResetToken(null);
                removeAccountLinkToken('reset');
              }}
            >
              Sign in
            </button>
            <button
              type="button"
              role="tab"
              aria-selected={mode === 'register'}
              disabled={automaticVerificationPending}
              onClick={() => {
                setMode('register');
                setResetToken(null);
                setAutomaticVerificationState('idle');
                removeAccountLinkToken('reset');
              }}
            >
              Create account
            </button>
          </div>
          <h2 id="account-form-title">
            {automaticVerificationPending
              ? 'Verifying your email'
              : resetToken
                ? 'Choose a new password'
                : mode === 'sign-in'
                  ? 'Welcome back'
                  : 'Start with a code'}
          </h2>
          {error && <ErrorNotice message={error} />}
          {automaticVerificationPending ? null : mode === 'sign-in' ? (
            resetToken ? (
              <form className="inline-account-flow" onSubmit={(event) => void completeReset(event)}>
                <label htmlFor="reset-new-password">New password</label>
                <input
                  id="reset-new-password"
                  type="password"
                  autoComplete="new-password"
                  minLength={12}
                  maxLength={128}
                  required
                  value={resetPassword}
                  onChange={(event) => setResetPassword(event.target.value)}
                />
                <button type="submit" disabled={busy}>
                  {busy ? 'Changing password…' : 'Set new password'}
                </button>
                <button
                  type="button"
                  className="text-action"
                  disabled={busy}
                  onClick={() => {
                    setResetToken(null);
                    removeAccountLinkToken('reset');
                  }}
                >
                  Back to sign in
                </button>
              </form>
            ) : (
              <>
                <form onSubmit={(event) => void signIn(event)}>
                  <label htmlFor="account-email">Adult email</label>
                  <input
                    id="account-email"
                    type="email"
                    autoComplete="email"
                    required
                    value={email}
                    onChange={(event) => setEmail(event.target.value)}
                  />
                  <label htmlFor="account-password">Password</label>
                  <input
                    id="account-password"
                    type="password"
                    autoComplete="current-password"
                    required
                    value={password}
                    onChange={(event) => setPassword(event.target.value)}
                  />
                  <button type="submit" disabled={busy}>
                    {busy ? 'Signing in…' : 'Sign in'}
                  </button>
                  <button
                    className="text-action"
                    type="button"
                    disabled={busy}
                    onClick={() => void requestReset()}
                  >
                    Forgot password?
                  </button>
                  <p className="account-recovery-note">
                    Enter the adult email above to receive a new verification link.
                  </p>
                  <button
                    className="text-action"
                    type="button"
                    disabled={busy}
                    onClick={() => void resendVerification()}
                  >
                    Send a new verification
                  </button>
                  {verificationToken && (
                    <button
                      type="button"
                      className="secondary-action"
                      disabled={busy}
                      onClick={() => void verifyLocalAccount()}
                    >
                      Verify email
                    </button>
                  )}
                </form>
              </>
            )
          ) : !registrationToken ? (
            <form onSubmit={(event) => void checkCode(event)}>
              <label htmlFor="animal-code">Animal code</label>
              <input
                id="animal-code"
                autoComplete="off"
                required
                value={code}
                onChange={(event) => setCode(event.target.value.toUpperCase())}
                placeholder="PENGUIN-TRAIL"
              />
              <button type="submit" disabled={busy}>
                {busy ? 'Checking…' : 'Continue'}
              </button>
            </form>
          ) : (
            <form onSubmit={(event) => void register(event)}>
              <label htmlFor="register-first-name">Your first name</label>
              <input
                id="register-first-name"
                required
                maxLength={40}
                value={firstName}
                onChange={(event) => setFirstName(event.target.value)}
              />
              <label htmlFor="register-email">Adult email</label>
              <input
                id="register-email"
                type="email"
                autoComplete="email"
                required
                value={email}
                onChange={(event) => setEmail(event.target.value)}
              />
              <label htmlFor="register-password">
                Password <span>12 or more characters</span>
              </label>
              <input
                id="register-password"
                type="password"
                autoComplete="new-password"
                minLength={12}
                maxLength={128}
                required
                value={password}
                onChange={(event) => setPassword(event.target.value)}
              />
              <label className="adult-confirmation">
                <input type="checkbox" required />
                <span>I confirm I am 18 or older and am creating this family account.</span>
              </label>
              {policyVersion && (
                <label className="adult-confirmation">
                  <input type="checkbox" required />
                  <span>
                    I agree to the <a href="/terms">Terms of Use</a> and acknowledge the{' '}
                    <a href="/privacy">Privacy Notice</a> (version {policyVersion}).
                  </span>
                </label>
              )}
              <button type="submit" disabled={busy || Boolean(verificationToken)}>
                {busy ? 'Creating…' : 'Create account'}
              </button>
              {verificationToken && (
                <button
                  type="button"
                  className="secondary-action"
                  disabled={busy}
                  onClick={() => void verifyLocalAccount()}
                >
                  Verify email
                </button>
              )}
              {registrationComplete && (
                <button
                  type="button"
                  className="text-action"
                  disabled={busy}
                  onClick={() => void resendVerification()}
                >
                  Send a new verification
                </button>
              )}
            </form>
          )}
          {message && (
            <p className="account-message" role="status">
              {message}
            </p>
          )}
        </section>
      </main>
    </div>
  );
}

export function AccountDashboard({
  account,
  animals,
  explorers,
  onBack,
  onOpenAnimal,
  onOpenTrailBook,
  onAccountChange,
  onAnimalsChange,
  onExplorersChange,
  onLogout,
}: {
  account: Account;
  animals: AccountAnimal[];
  explorers: AccountExplorer[];
  onBack: () => void;
  onOpenAnimal: (entitlementId: string) => void;
  onOpenTrailBook: () => void;
  onAccountChange: (account: Account) => void;
  onAnimalsChange: (animals: AccountAnimal[]) => void;
  onExplorersChange: (explorers: AccountExplorer[]) => void;
  onLogout: (reason?: 'deletion_requested') => void;
}) {
  const [code, setCode] = useState('');
  const [message, setMessage] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [selectedExplorerId, setSelectedExplorerId] = useState(explorers[0]?.id ?? '');
  const [firstName, setFirstName] = useState(account.firstName);
  const [distanceUnit, setDistanceUnit] = useState<DistanceUnit>(account.distanceUnit);
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [deletionPassword, setDeletionPassword] = useState('');
  const [privacyExportPassword, setPrivacyExportPassword] = useState('');
  const [deletionConfirmation, setDeletionConfirmation] = useState('');
  const [newExplorerNickname, setNewExplorerNickname] = useState('');
  const [newExplorerBadge, setNewExplorerBadge] = useState<AccountExplorer['badge']>('sun');
  const [showExplorerForm, setShowExplorerForm] = useState(false);
  const [eligibleExplorerAnimals, setEligibleExplorerAnimals] = useState<string[]>([]);
  const [explorerCreationKey, setExplorerCreationKey] = useState<string | null>(null);
  const [explorerNickname, setExplorerNickname] = useState(() => explorers[0]?.label ?? '');
  const [explorerBadge, setExplorerBadge] = useState<AccountExplorer['badge']>(
    () => explorers[0]?.badge ?? 'sun',
  );
  const [explorerDeletionConfirmation, setExplorerDeletionConfirmation] = useState('');
  const codeInput = useRef<HTMLInputElement>(null);
  const selectedExplorer =
    explorers.find((explorer) => explorer.id === selectedExplorerId) ?? explorers[0];
  const selectedAnimals = selectedExplorer
    ? animals.filter((animal) => animal.explorerId === selectedExplorer.id)
    : [];
  const familyAnimals = familyAnimalsForTable(animals, selectedExplorer?.id);

  async function redeem(event: FormEvent) {
    event.preventDefault();
    setBusy(true);
    setMessage(null);
    try {
      if (!selectedExplorer) throw new Error('Add an explorer profile before entering a code.');
      const result = await api.redeemAccountAnimal(code, selectedExplorer.id);
      const [loadedAnimals, loadedExplorers] = await Promise.all([
        api.listAccountAnimals(),
        api.listAccountExplorers(),
      ]);
      onAnimalsChange(loadedAnimals);
      onExplorersChange(loadedExplorers);
      setCode('');
      setMessage(
        result.status === 'unlocked'
          ? 'A new animal trail is unlocked!'
          : 'That animal is already unlocked.',
      );
    } catch (reason) {
      setMessage(messageFrom(reason));
    } finally {
      setBusy(false);
    }
  }

  async function addExplorer() {
    setBusy(true);
    setMessage(null);
    try {
      const idempotencyKey = explorerCreationKey ?? crypto.randomUUID();
      if (!explorerCreationKey) setExplorerCreationKey(idempotencyKey);
      const created = await api.createAccountExplorer({
        nickname: newExplorerNickname.trim(),
        badge: newExplorerBadge,
        idempotencyKey,
      });
      const [loadedAnimals, loadedExplorers] = await Promise.all([
        api.listAccountAnimals(),
        api.listAccountExplorers(),
      ]);
      onAnimalsChange(loadedAnimals);
      onExplorersChange(loadedExplorers);
      setSelectedExplorerId(created.id);
      setExplorerNickname(created.label);
      setExplorerBadge(created.badge);
      setShowExplorerForm(false);
      setNewExplorerNickname('');
      setExplorerCreationKey(null);
      setMessage(`${created.label} is ready with their inherited animal access.`);
    } catch (reason) {
      setMessage(messageFrom(reason));
    } finally {
      setBusy(false);
    }
  }

  async function saveName(event: FormEvent) {
    event.preventDefault();
    setBusy(true);
    setMessage(null);
    try {
      onAccountChange(await api.updateAccount({ firstName }));
      setMessage('Account name updated.');
    } catch (reason) {
      setMessage(messageFrom(reason));
    } finally {
      setBusy(false);
    }
  }

  async function saveDistanceUnit(event: FormEvent) {
    event.preventDefault();
    setBusy(true);
    setMessage(null);
    try {
      onAccountChange(await api.updateAccount({ distanceUnit }));
      setMessage(
        distanceUnit === 'miles'
          ? 'Distances will be shown in miles.'
          : 'Distances will be shown in kilometers.',
      );
    } catch (reason) {
      setMessage(messageFrom(reason));
    } finally {
      setBusy(false);
    }
  }

  async function saveExplorer(event: FormEvent) {
    event.preventDefault();
    if (!selectedExplorer) return;
    setBusy(true);
    setMessage(null);
    try {
      const updated = await api.updateAccountExplorer(selectedExplorer.id, {
        nickname: explorerNickname.trim(),
        badge: explorerBadge,
      });
      onExplorersChange(
        explorers.map((explorer) => (explorer.id === updated.id ? updated : explorer)),
      );
      onAnimalsChange(await api.listAccountAnimals());
      setMessage(`${updated.label}'s profile was updated.`);
    } catch (reason) {
      setMessage(messageFrom(reason));
    } finally {
      setBusy(false);
    }
  }

  async function deleteExplorer(event: FormEvent) {
    event.preventDefault();
    if (!selectedExplorer || explorerDeletionConfirmation !== 'DELETE') return;
    setBusy(true);
    setMessage(null);
    try {
      await api.deleteAccountExplorer(selectedExplorer.id);
      const [loadedAnimals, loadedExplorers] = await Promise.all([
        api.listAccountAnimals(),
        api.listAccountExplorers(),
      ]);
      onAnimalsChange(loadedAnimals);
      onExplorersChange(loadedExplorers);
      setSelectedExplorerId(loadedExplorers[0]?.id ?? '');
      setExplorerNickname('');
      setExplorerBadge(loadedExplorers[0]?.badge ?? 'sun');
      setMessage('Explorer profile, animal access, and progress were permanently removed.');
    } catch (reason) {
      setMessage(messageFrom(reason));
    } finally {
      setBusy(false);
    }
  }

  async function savePassword(event: FormEvent) {
    event.preventDefault();
    setBusy(true);
    try {
      await api.changeAccountPassword(currentPassword, newPassword);
      setCurrentPassword('');
      setNewPassword('');
      setMessage('Password updated. Other sessions were signed out.');
    } catch (reason) {
      setMessage(messageFrom(reason));
    } finally {
      setBusy(false);
    }
  }

  async function logout() {
    setBusy(true);
    try {
      await api.logoutAccount();
      onLogout();
    } catch (reason) {
      setMessage(messageFrom(reason));
      setBusy(false);
    }
  }

  async function requestDeletion(event: FormEvent) {
    event.preventDefault();
    setBusy(true);
    setMessage(null);
    try {
      await api.requestAccountDeletion(deletionPassword, deletionConfirmation);
      onLogout('deletion_requested');
    } catch (reason) {
      setMessage(messageFrom(reason));
      setBusy(false);
    }
  }

  async function exportAccountData(event: FormEvent) {
    event.preventDefault();
    setBusy(true);
    setMessage(null);
    try {
      const blob = await api.downloadAccountData(privacyExportPassword);
      const url = URL.createObjectURL(blob);
      const anchor = document.createElement('a');
      anchor.href = url;
      anchor.download = 'wild-trails-account-data.json';
      anchor.click();
      URL.revokeObjectURL(url);
      setPrivacyExportPassword('');
      setMessage('Your account data copy was downloaded.');
    } catch (reason) {
      setMessage(messageFrom(reason));
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="account-shell">
      <header className="account-topbar">
        <button className="back-button" type="button" onClick={onBack}>
          ← Shop
        </button>
        <button className="parent-link" type="button" disabled={busy} onClick={() => void logout()}>
          Sign out
        </button>
      </header>
      <main className="account-dashboard">
        <section className="account-dashboard-heading" aria-labelledby="account-dashboard-title">
          <div>
            <p className="eyebrow">Grown-up area</p>
            <h1 id="account-dashboard-title">Hello, {account.firstName}.</h1>
            <p>
              {account.email} <span aria-hidden="true">·</span>{' '}
              {account.emailVerifiedAt ? 'Email verified' : 'Email not verified'}
            </p>
          </div>
          <button type="button" className="trail-book-launch" onClick={onOpenTrailBook}>
            <span aria-hidden="true">✦</span>
            <span>
              <strong>Open Trail Book</strong>
              <small>Maps, stories, quizzes, and animal adventures</small>
            </span>
            <span aria-hidden="true">→</span>
          </button>
        </section>

        {message && (
          <p className="account-message" role="status">
            {message}
          </p>
        )}

        <section>
          <section className="explorer-collection" aria-labelledby="account-animals-title">
            <div className="section-heading-row account-explorer-heading">
              <div>
                <p className="eyebrow">Bracelets and explorers</p>
                <h2 id="account-animals-title">Manage animal access</h2>
              </div>
              <button
                type="button"
                className="account-outline-action"
                disabled={busy}
                onClick={() => {
                  setShowExplorerForm(true);
                  setExplorerCreationKey(crypto.randomUUID());
                  void api
                    .getAccountExplorerCreationPreview()
                    .then((preview) =>
                      setEligibleExplorerAnimals(
                        preview.eligibleAnimals.map((animal) => animal.name),
                      ),
                    )
                    .catch((reason) => setMessage(messageFrom(reason)));
                }}
              >
                <span aria-hidden="true">＋</span> Add explorer
              </button>
            </div>
            <p className="explorer-privacy-note">
              Explorer profiles keep learning progress separate. Animals are shared across the
              family collection. A nickname is shown only in this family account and Trail Book.
            </p>

            {showExplorerForm && (
              <form
                className="account-form-card account-explorer-create-form"
                onSubmit={(event) => {
                  event.preventDefault();
                  void addExplorer();
                }}
              >
                <h3>Add an explorer</h3>
                <label htmlFor="explorer-nickname">Nickname</label>
                <input
                  id="explorer-nickname"
                  required
                  maxLength={24}
                  value={newExplorerNickname}
                  onChange={(event) => setNewExplorerNickname(event.target.value)}
                />
                <label htmlFor="explorer-badge">Icon</label>
                <select
                  id="explorer-badge"
                  value={newExplorerBadge}
                  onChange={(event) =>
                    setNewExplorerBadge(event.target.value as AccountExplorer['badge'])
                  }
                >
                  {Object.keys(EXPLORER_BADGE_SYMBOLS).map((badge) => (
                    <option key={badge} value={badge}>
                      {EXPLORER_BADGE_SYMBOLS[badge as AccountExplorer['badge']]} {badge}
                    </option>
                  ))}
                </select>
                <p>
                  {eligibleExplorerAnimals.length
                    ? `This explorer will receive: ${eligibleExplorerAnimals.join(', ')}.`
                    : 'No eligible bracelet access will be added yet.'}
                </p>
                <div className="account-inline-actions">
                  <button type="submit" disabled={busy}>
                    Create explorer
                  </button>
                  <button
                    type="button"
                    className="text-action"
                    onClick={() => {
                      setShowExplorerForm(false);
                      setExplorerCreationKey(null);
                    }}
                  >
                    Cancel
                  </button>
                </div>
              </form>
            )}

            <div className="explorer-tabs" role="tablist" aria-label="Explorer profiles">
              {explorers.map((explorer) => (
                <button
                  key={explorer.id}
                  type="button"
                  role="tab"
                  aria-selected={explorer.id === selectedExplorer?.id}
                  className={explorer.id === selectedExplorer?.id ? 'is-selected' : ''}
                  onClick={() => {
                    setSelectedExplorerId(explorer.id);
                    setExplorerNickname(explorer.label);
                    setExplorerBadge(explorer.badge);
                    setExplorerDeletionConfirmation('');
                  }}
                >
                  <span aria-hidden="true">{EXPLORER_BADGE_SYMBOLS[explorer.badge]}</span>
                  <span>
                    <strong>{explorer.label}</strong>
                    <small>
                      {explorer.explorerNumber === 1 && <b>Primary</b>}
                      {explorer.animalCount} animal{explorer.animalCount === 1 ? '' : 's'}
                    </small>
                  </span>
                </button>
              ))}
            </div>

            {selectedExplorer ? (
              <div className="selected-explorer-panel">
                <div className="selected-explorer-identity">
                  <span aria-hidden="true">{EXPLORER_BADGE_SYMBOLS[selectedExplorer.badge]}</span>
                  <div>
                    <h3>
                      {selectedExplorer.label}
                      {selectedExplorer.explorerNumber === 1
                        ? ' — Primary explorer'
                        : ' — Explorer'}
                    </h3>
                    <p>
                      {selectedExplorer.explorerNumber === 1
                        ? 'Animals are shared across all explorers. The primary explorer manages the family collection and can be renamed, but not deleted.'
                        : `${selectedExplorer.label} has ${selectedAnimals.length} animal${selectedAnimals.length === 1 ? '' : 's'} and separate learning progress.`}
                    </p>
                  </div>
                </div>
                <div className="selected-explorer-actions">
                  <details className="explorer-rename-control">
                    <summary>
                      <span aria-hidden="true">✎</span> Rename explorer
                    </summary>
                    <form onSubmit={(event) => void saveExplorer(event)}>
                      <label htmlFor="selected-explorer-nickname">Nickname</label>
                      <input
                        id="selected-explorer-nickname"
                        required
                        maxLength={24}
                        value={explorerNickname || selectedExplorer.label}
                        onChange={(event) => setExplorerNickname(event.target.value)}
                      />
                      <label htmlFor="selected-explorer-badge">Icon</label>
                      <select
                        id="selected-explorer-badge"
                        value={explorerNickname ? explorerBadge : selectedExplorer.badge}
                        onChange={(event) => {
                          setExplorerNickname((current) => current || selectedExplorer.label);
                          setExplorerBadge(event.target.value as AccountExplorer['badge']);
                        }}
                      >
                        {Object.keys(EXPLORER_BADGE_SYMBOLS).map((badge) => (
                          <option key={badge} value={badge}>
                            {EXPLORER_BADGE_SYMBOLS[badge as AccountExplorer['badge']]} {badge}
                          </option>
                        ))}
                      </select>
                      <button type="submit" disabled={busy}>
                        Save explorer
                      </button>
                    </form>
                  </details>
                  {selectedExplorer.explorerNumber === 1 ? (
                    <div className="primary-explorer-delete">
                      <button
                        type="button"
                        className="account-outline-action"
                        disabled
                        aria-describedby="primary-explorer-delete-note"
                      >
                        <span aria-hidden="true">♲</span> Delete explorer
                      </button>
                      <p id="primary-explorer-delete-note">
                        The primary explorer cannot be deleted.
                      </p>
                    </div>
                  ) : (
                    <details className="explorer-delete-control">
                      <summary>
                        <span aria-hidden="true">♲</span> Delete explorer
                      </summary>
                      <p>
                        This permanently removes {selectedExplorer.label}'s profile, animal access,
                        and learning progress. This cannot be undone.
                      </p>
                      <form onSubmit={(event) => void deleteExplorer(event)}>
                        <label htmlFor="explorer-deletion-confirmation">
                          Type DELETE to confirm
                        </label>
                        <input
                          id="explorer-deletion-confirmation"
                          required
                          pattern="DELETE"
                          value={explorerDeletionConfirmation}
                          onChange={(event) => setExplorerDeletionConfirmation(event.target.value)}
                        />
                        <button
                          type="submit"
                          className="danger-button"
                          disabled={busy || explorerDeletionConfirmation !== 'DELETE'}
                        >
                          Permanently delete explorer
                        </button>
                      </form>
                    </details>
                  )}
                </div>
              </div>
            ) : (
              <div className="empty-explorer-card">
                <h3>Add an explorer to begin.</h3>
                <p>Explorer profiles keep each person’s learning progress separate.</p>
              </div>
            )}
          </section>
        </section>

        <section>
          <section className="account-animal-collection" aria-labelledby="shared-animals-title">
            <div className="account-collection-heading">
              <h2 id="shared-animals-title">Shared animal friends</h2>
              <p>This collection is shared by all explorers in your family.</p>
            </div>
            {familyAnimals.length ? (
              <div className="account-animal-table-wrap">
                <table className="account-animal-table">
                  <caption className="visually-hidden">
                    Shared animal friends in this family account
                  </caption>
                  <thead>
                    <tr>
                      <th scope="col">Animal</th>
                      <th scope="col">Type</th>
                      <th scope="col">Personality</th>
                      <th scope="col">Trail status</th>
                      <th scope="col">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {familyAnimals.map((animal) => {
                      const name = animal.animalName ?? animal.productName;
                      const meta = ACCOUNT_ANIMAL_TABLE_META[animal.speciesId];
                      return (
                        <tr key={animal.entitlementId}>
                          <th scope="row" data-label="Animal">
                            <span className="account-animal-table-identity">
                              <span className="account-animal-table-icon" aria-hidden="true">
                                {meta && <img src={meta.iconUrl} alt="" />}
                              </span>
                              <span>{name}</span>
                            </span>
                          </th>
                          <td data-label="Type">{meta?.type ?? animal.productName}</td>
                          <td data-label="Personality">
                            {animal.personality ?? animal.productDescription}
                          </td>
                          <td data-label="Trail status">
                            <span className="account-trail-status">Ready in Trail Book</span>
                          </td>
                          <td data-label="Actions">
                            <div className="account-animal-actions">
                              <button
                                type="button"
                                onClick={createOpenAccountAnimalHandler(
                                  animal.entitlementId,
                                  onOpenAnimal,
                                )}
                              >
                                View
                              </button>
                              <details className="account-animal-overflow">
                                <summary aria-label={`More actions for ${name}`}>⋮</summary>
                                <div>
                                  <button
                                    type="button"
                                    onClick={createOpenAccountAnimalHandler(
                                      animal.entitlementId,
                                      onOpenAnimal,
                                    )}
                                  >
                                    Open {name}’s trail
                                  </button>
                                </div>
                              </details>
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            ) : (
              <div className="account-animal-empty">
                <h3>No animal friends yet.</h3>
                <p>
                  Enter the animal code that came with a bracelet to add the first family animal.
                </p>
                <button
                  type="button"
                  onClick={() => {
                    codeInput.current?.focus();
                    codeInput.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
                  }}
                >
                  Enter animal code
                </button>
              </div>
            )}
          </section>
          <section className="account-management-grid" aria-label="Family account management">
            <form
              className="account-form-card account-unlock-card"
              onSubmit={(event) => void redeem(event)}
            >
              <h2>Add animal to family collection</h2>
              <p>
                Add a new animal using an animal code. The animal will be added to{' '}
                {selectedExplorer?.label ?? 'the selected explorer'} and shown in the family
                collection.
              </p>
              <label htmlFor="portal-animal-code">Animal code</label>
              <input
                ref={codeInput}
                id="portal-animal-code"
                required
                value={code}
                onChange={(event) => setCode(event.target.value.toUpperCase())}
                placeholder="Enter animal code (e.g., PERCY-4827)"
              />
              <button type="submit" disabled={busy || !selectedExplorer}>
                Unlock animal
              </button>
              <p className="account-guidance-note">
                <span aria-hidden="true">ⓘ</span> Each animal can be unlocked once per explorer.
              </p>
            </form>
          </section>
        </section>

        <section>
          <div className="account-management-grid" aria-label="Family account management">
            <div className="account-settings account-form-card">
              <h2>Account details</h2>
              <form onSubmit={(event) => void saveName(event)}>
                <label htmlFor="settings-first-name">First name</label>
                <input
                  id="settings-first-name"
                  required
                  maxLength={40}
                  value={firstName}
                  onChange={(event) => setFirstName(event.target.value)}
                />
                <button type="submit" disabled={busy}>
                  Save name
                </button>
              </form>
              <form onSubmit={(event) => void saveDistanceUnit(event)}>
                <label htmlFor="settings-distance-unit">Distance units</label>
                <select
                  id="settings-distance-unit"
                  value={distanceUnit}
                  onChange={(event) => setDistanceUnit(event.target.value as DistanceUnit)}
                >
                  <option value="miles">Miles (mi)</option>
                  <option value="kilometers">Kilometers (km)</option>
                </select>
                <button type="submit" disabled={busy || distanceUnit === account.distanceUnit}>
                  Save distance units
                </button>
              </form>
              <details className="account-settings-row">
                <summary>
                  <span aria-hidden="true">♙</span> Change password <b aria-hidden="true">›</b>
                </summary>
                <form onSubmit={(event) => void savePassword(event)}>
                  <label htmlFor="current-password">Current password</label>
                  <input
                    id="current-password"
                    type="password"
                    autoComplete="current-password"
                    required
                    value={currentPassword}
                    onChange={(event) => setCurrentPassword(event.target.value)}
                  />
                  <label htmlFor="new-password">New password</label>
                  <input
                    id="new-password"
                    type="password"
                    autoComplete="new-password"
                    minLength={12}
                    maxLength={128}
                    required
                    value={newPassword}
                    onChange={(event) => setNewPassword(event.target.value)}
                  />
                  <button type="submit" disabled={busy}>
                    Change password
                  </button>
                </form>
              </details>
              <details className="account-settings-row">
                <summary>
                  <span aria-hidden="true">⇩</span> Download my account data{' '}
                  <b aria-hidden="true">›</b>
                </summary>
                <p>
                  Download account settings, Explorer profiles, animal access, redemption history,
                  and saved animal-fact quiz attempts. Shared animal simulations are not part of an
                  individual account export.
                </p>
                <form onSubmit={(event) => void exportAccountData(event)}>
                  <label htmlFor="privacy-export-password">Current password</label>
                  <input
                    id="privacy-export-password"
                    type="password"
                    autoComplete="current-password"
                    required
                    value={privacyExportPassword}
                    onChange={(event) => setPrivacyExportPassword(event.target.value)}
                  />
                  <button type="submit" disabled={busy}>
                    Download JSON copy
                  </button>
                </form>
              </details>
              <details className="account-settings-row account-deletion-control">
                <summary>
                  <span aria-hidden="true">♲</span> Delete account <b aria-hidden="true">›</b>
                </summary>
                <p>
                  This signs you out, removes profile access, revokes animal trails, and schedules
                  personal data removal. Required historical and security records are retained
                  without account contact details.
                </p>
                <form onSubmit={(event) => void requestDeletion(event)}>
                  <label htmlFor="deletion-password">Current password</label>
                  <input
                    id="deletion-password"
                    type="password"
                    autoComplete="current-password"
                    required
                    value={deletionPassword}
                    onChange={(event) => setDeletionPassword(event.target.value)}
                  />
                  <label htmlFor="deletion-confirmation">Type DELETE to confirm</label>
                  <input
                    id="deletion-confirmation"
                    required
                    pattern="DELETE"
                    autoComplete="off"
                    value={deletionConfirmation}
                    onChange={(event) => setDeletionConfirmation(event.target.value)}
                  />
                  <button type="submit" className="danger-button" disabled={busy}>
                    Request account deletion
                  </button>
                </form>
              </details>
            </div>
          </div>
        </section>
      </main>
    </div>
  );
}
